import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Calendar, PauseCircle, Users, ChevronDown, ChevronUp } from 'lucide-react';
import { useCrud } from '../../../api/hooks/useCrud';
import { api } from '../../../api/client';
import { ROUTES } from '../../../api/routes';
import { useAuthStore } from '../../../store/authStore';
import { useHojeReferencia } from '../../../api/hooks/useSimClock';
import { getDiaSemanaFromDate } from '../../../api/hooks/useHorarios';
import type { Professor, Turno } from '../../../types/models';
import type { OcupacaoProfessorSlot } from '../grade-conflito';
import { DIAS_OPTIONS } from '../turnos-shared';
import {
  buildDisponibilidadeDiaTurno,
  diasDisponiveisNosTurnos,
  type TurnoCor,
} from './turnosOciosos';

type Props = {
  turnos: Turno[];
  /** Turno marcado nas abas da listagem (null = Todos) */
  selectedTurnoId?: string | null;
  /** Turno da hora atual (★) — usado quando a lista está em Todos */
  turnoPreferidoId?: string | null;
  /** Espelha o clique do relatório nas abas de cima */
  onTurnoChange?: (turnoId: string) => void;
};

function nomeCurto(nome: string): string {
  const parts = nome.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 2) return nome.trim();
  return `${parts[0]} ${parts[parts.length - 1]}`;
}

function labelDia(dia: string): string {
  return DIAS_OPTIONS.find((o) => o.value === dia)?.label ?? dia.slice(0, 3);
}

function Chip({
  ativo,
  onClick,
  children,
  cor,
}: {
  ativo: boolean;
  onClick: () => void;
  children: ReactNode;
  cor?: TurnoCor;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: 30,
        padding: '0 10px',
        borderRadius: 8,
        fontSize: '0.72rem',
        fontWeight: 700,
        cursor: 'pointer',
        border: `1.5px solid ${ativo ? cor?.border ?? '#2563eb' : '#e2e8f0'}`,
        background: ativo ? cor?.headBg ?? '#eff6ff' : '#fff',
        color: ativo ? cor?.headFg ?? '#1d4ed8' : '#64748b',
      }}
    >
      {children}
    </button>
  );
}

export function TurnosOciososReport({
  turnos,
  selectedTurnoId = null,
  turnoPreferidoId = null,
  onTurnoChange,
}: Props) {
  const escolaId = useAuthStore((s) => s.escolaId);
  const { data: professores = [], isLoading: loadingProfs } = useCrud<Professor>('professores');
  const { hojeStr, isSimulation } = useHojeReferencia();
  const [expanded, setExpanded] = useState(false);

  const diasCols = useMemo(() => diasDisponiveisNosTurnos(turnos), [turnos]);
  const diaDefault = useMemo(() => {
    const fromClock = getDiaSemanaFromDate(hojeStr);
    if (fromClock && diasCols.includes(fromClock)) return fromClock;
    return diasCols[0] ?? 'SEGUNDA';
  }, [hojeStr, diasCols]);

  const turnosAtivos = useMemo(
    () =>
      [...turnos]
        .filter((t) => t.ativo !== false)
        .sort((a, b) =>
          String(a.horaAberturaPortoes || a.horaEntradaOficial || '').localeCompare(
            String(b.horaAberturaPortoes || b.horaEntradaOficial || ''),
          ),
        ),
    [turnos],
  );

  /** Espelha a aba de cima: selecionado, senão ★ da hora, senão o 1º. */
  const turnoId = useMemo(() => {
    const ids = new Set(turnosAtivos.map((t) => t.id));
    if (selectedTurnoId && ids.has(selectedTurnoId)) return selectedTurnoId;
    if (turnoPreferidoId && ids.has(turnoPreferidoId)) return turnoPreferidoId;
    return turnosAtivos[0]?.id ?? '';
  }, [selectedTurnoId, turnoPreferidoId, turnosAtivos]);

  const escolherTurno = (id: string) => {
    onTurnoChange?.(id);
  };

  const [diaSemana, setDiaSemana] = useState(diaDefault);
  const [soComFolga, setSoComFolga] = useState(true);
  const [ocupacao, setOcupacao] = useState<OcupacaoProfessorSlot[]>([]);
  const [loadingOcup, setLoadingOcup] = useState(false);

  useEffect(() => {
    setDiaSemana(diaDefault);
  }, [diaDefault]);

  useEffect(() => {
    if (!escolaId) return;
    let cancelled = false;
    setLoadingOcup(true);
    api
      .get(ROUTES.horarios.ocupacao(escolaId))
      .then(({ data }) => {
        if (!cancelled) setOcupacao(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setOcupacao([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingOcup(false);
      });
    return () => {
      cancelled = true;
    };
  }, [escolaId, turnos]);

  const relatorio = useMemo(
    () =>
      buildDisponibilidadeDiaTurno(turnos, professores, ocupacao, {
        turnoId,
        diaSemana,
        soComFolga,
      }),
    [turnos, professores, ocupacao, turnoId, diaSemana, soComFolga],
  );

  const dataLabel = useMemo(() => {
    const [y, m, d] = hojeStr.split('-').map(Number);
    const dt = new Date(y, (m || 1) - 1, d || 1);
    return new Intl.DateTimeFormat('pt-BR', {
      weekday: 'short',
      day: '2-digit',
      month: '2-digit',
    }).format(dt);
  }, [hojeStr]);

  const loading = loadingProfs || loadingOcup;
  const { linhas, parados, totalAtivos, totalComCarga, turnos: turnosOp, livresPorAula } = relatorio;
  const turnoSel = turnosOp.find((t) => t.turnoId === turnoId) ?? turnosOp[0];
  const cor = turnoSel?.cor;
  const slots = turnoSel?.slots ?? [];

  return (
    <section
      className="ac-turnos-ociosos"
      style={{
        marginTop: 16,
        background: 'var(--bg-card, white)',
        border: '1px solid var(--border-color, #e5e7eb)',
        borderRadius: 12,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 14px',
          borderBottom: '1px solid var(--border-color, #e5e7eb)',
          background: 'var(--bg-main, #f8fafc)',
        }}
      >
        <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setExpanded(!expanded)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', border: '1px solid var(--border-color, #e5e7eb)', background: 'white', color: 'var(--text-main)', cursor: 'pointer' }}>
            {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          <div style={{ cursor: 'pointer' }} onClick={() => setExpanded(!expanded)}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 700, fontSize: '0.9rem', color: 'var(--text-main)' }}>
              <Users size={16} aria-hidden />
              Quem está livre na grade
            </div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 3 }}>
              Turno segue a aba de cima (★ se estiver em Todos). Célula colorida = livre; ponto = ocupado.
            </div>
          </div>
        </div>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            fontSize: '0.7rem',
            fontWeight: 700,
            padding: '4px 9px',
            borderRadius: 8,
            background: isSimulation ? '#f5f3ff' : '#f1f5f9',
            color: isSimulation ? '#6d28d9' : '#475569',
            border: `1px solid ${isSimulation ? '#ddd6fe' : '#e2e8f0'}`,
            textTransform: 'capitalize',
          }}
          title={isSimulation ? `Dia simulado ${hojeStr}` : `Dia ${hojeStr}`}
        >
          <Calendar size={12} aria-hidden />
          {isSimulation ? 'Dia sim' : 'Hoje'}: {dataLabel}
        </span>
      </div>

      {expanded && (
      <div style={{ padding: 14 }}>
        {loading ? (
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Carregando ocupação…</div>
        ) : totalAtivos === 0 ? (
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>Nenhum professor cadastrado na escola.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {/* Controles: turno + dia */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', marginRight: 2 }}>Turno</span>
                {turnosOp.map((t) => (
                  <Chip
                    key={t.turnoId}
                    ativo={t.turnoId === turnoSel?.turnoId}
                    onClick={() => escolherTurno(t.turnoId)}
                    cor={t.cor}
                  >
                    <span
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: 2,
                        background: t.cor.bg,
                        border: `1px solid ${t.cor.border}`,
                      }}
                    />
                    {t.turnoNome}
                  </Chip>
                ))}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#64748b', marginRight: 2 }}>Dia</span>
                {diasCols.map((d) => (
                  <Chip key={d} ativo={d === diaSemana} onClick={() => setDiaSemana(d)} cor={cor}>
                    {labelDia(d)}
                    {d === diaDefault ? (
                      <span style={{ fontSize: '0.55rem', opacity: 0.8 }}>sim</span>
                    ) : null}
                  </Chip>
                ))}
                <label
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    marginLeft: 8,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: '#475569',
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={soComFolga}
                    onChange={(e) => setSoComFolga(e.target.checked)}
                  />
                  Só quem tem folga
                </label>
              </div>
            </div>

            {parados.length > 0 && (
              <div
                style={{
                  border: '1px solid #fdba74',
                  borderRadius: 10,
                  background: '#fff7ed',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 12px',
                    borderBottom: '1px solid #fed7aa',
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    color: '#9a3412',
                  }}
                >
                  <PauseCircle size={15} aria-hidden />
                  Parados — sem nenhuma aula na grade
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: '0.68rem',
                      fontWeight: 800,
                      padding: '2px 8px',
                      borderRadius: 999,
                      background: '#ffedd5',
                      color: '#c2410c',
                      border: '1px solid #fdba74',
                    }}
                  >
                    {parados.length}/{totalAtivos}
                  </span>
                </div>
                <div style={{ padding: '10px 12px', display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {parados.map((p) => (
                    <span
                      key={p.id}
                      title={p.nome}
                      style={{
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        padding: '2px 7px',
                        borderRadius: 999,
                        background: '#ffedd5',
                        color: '#9a3412',
                        border: '1px solid #fdba74',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {nomeCurto(p.nome)}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Resumo por aula: quantos livres */}
            {slots.length > 0 && (
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 6,
                  alignItems: 'center',
                  padding: '8px 10px',
                  borderRadius: 8,
                  background: cor?.headBg ?? '#f8fafc',
                  border: `1px solid ${cor?.border ?? '#e2e8f0'}`,
                }}
              >
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: cor?.headFg ?? '#475569' }}>
                  {turnoSel?.turnoNome} · {labelDia(diaSemana)} — livres por aula
                </span>
                {slots.map((s, i) => (
                  <span
                    key={s.ordem}
                    title={`${s.ordem}ª · ${s.horaInicio}–${s.horaFim}`}
                    style={{
                      fontSize: '0.68rem',
                      fontWeight: 800,
                      padding: '3px 8px',
                      borderRadius: 6,
                      background: cor?.bg ?? '#fff',
                      color: cor?.fg ?? '#166534',
                      border: `1px solid ${cor?.border ?? '#86efac'}`,
                    }}
                  >
                    {s.ordem}ª · {livresPorAula[i] ?? 0}
                  </span>
                ))}
                <span style={{ fontSize: '0.68rem', color: '#64748b', marginLeft: 'auto' }}>
                  {linhas.length} na lista · {totalComCarga} com carga
                </span>
              </div>
            )}

            {!turnoSel || slots.length === 0 ? (
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Turno sem template de aulas.</div>
            ) : linhas.length === 0 ? (
              <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>
                {soComFolga
                  ? 'Ninguém com folga neste turno/dia. Desmarque “Só quem tem folga” para ver todos.'
                  : 'Nenhum professor com aula na grade ainda.'}
              </div>
            ) : (
              <div
                style={{
                  border: '1px solid var(--border-color, #e5e7eb)',
                  borderRadius: 10,
                  overflow: 'hidden',
                }}
              >
                <div style={{ overflowX: 'auto', maxHeight: 480, overflowY: 'auto' }}>
                  <table
                    style={{
                      width: '100%',
                      borderCollapse: 'collapse',
                      fontSize: '0.78rem',
                      minWidth: 420,
                    }}
                  >
                    <thead>
                      <tr>
                        <th
                          style={{
                            position: 'sticky',
                            left: 0,
                            top: 0,
                            zIndex: 3,
                            textAlign: 'left',
                            padding: '9px 12px',
                            background: '#f1f5f9',
                            borderBottom: '1px solid #e2e8f0',
                            fontWeight: 700,
                            color: '#475569',
                            minWidth: 150,
                          }}
                        >
                          Professor
                        </th>
                        {slots.map((s) => (
                          <th
                            key={s.ordem}
                            style={{
                              position: 'sticky',
                              top: 0,
                              zIndex: 2,
                              textAlign: 'center',
                              padding: '8px 6px',
                              background: cor?.headBg ?? '#f1f5f9',
                              borderBottom: `1px solid ${cor?.border ?? '#e2e8f0'}`,
                              fontWeight: 800,
                              color: cor?.headFg ?? '#475569',
                              minWidth: 52,
                            }}
                            title={`${s.horaInicio}–${s.horaFim}`}
                          >
                            {s.ordem}ª
                            <div style={{ fontSize: '0.55rem', fontWeight: 600, opacity: 0.85 }}>
                              {s.horaInicio}
                            </div>
                          </th>
                        ))}
                        <th
                          style={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            textAlign: 'center',
                            padding: '9px 8px',
                            background: '#f1f5f9',
                            borderBottom: '1px solid #e2e8f0',
                            fontWeight: 700,
                            color: '#475569',
                            minWidth: 48,
                          }}
                        >
                          Livres
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {linhas.map((linha, idx) => (
                        <tr
                          key={linha.professor.id}
                          style={{ background: idx % 2 === 0 ? '#fff' : '#fafafa' }}
                        >
                          <td
                            style={{
                              position: 'sticky',
                              left: 0,
                              zIndex: 1,
                              padding: '8px 12px',
                              fontWeight: 600,
                              background: idx % 2 === 0 ? '#fff' : '#fafafa',
                              borderBottom: '1px solid #f1f5f9',
                              whiteSpace: 'nowrap',
                            }}
                            title={linha.professor.nome}
                          >
                            {nomeCurto(linha.professor.nome)}
                          </td>
                          {linha.aulas.map((a) => (
                            <td
                              key={a.ordem}
                              style={{
                                padding: '6px 4px',
                                textAlign: 'center',
                                borderBottom: '1px solid #f1f5f9',
                              }}
                            >
                              {a.livre ? (
                                <span
                                  title={`Livre · ${a.ordem}ª · ${a.horaInicio}–${a.horaFim}`}
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 26,
                                    height: 26,
                                    borderRadius: 6,
                                    fontSize: '0.72rem',
                                    fontWeight: 800,
                                    background: cor?.bg ?? '#dcfce7',
                                    color: cor?.fg ?? '#15803d',
                                    border: `1px solid ${cor?.border ?? '#86efac'}`,
                                  }}
                                >
                                  {a.ordem}
                                </span>
                              ) : (
                                <span
                                  title={`Ocupado · ${a.ordem}ª`}
                                  style={{
                                    display: 'inline-block',
                                    width: 8,
                                    height: 8,
                                    borderRadius: 99,
                                    background: '#e2e8f0',
                                  }}
                                />
                              )}
                            </td>
                          ))}
                          <td
                            style={{
                              padding: '8px',
                              textAlign: 'center',
                              fontWeight: 800,
                              fontSize: '0.78rem',
                              color: linha.nLivres > 0 ? cor?.fg ?? '#15803d' : '#94a3b8',
                              borderBottom: '1px solid #f1f5f9',
                            }}
                          >
                            {linha.nLivres}/{slots.length}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </section>
  );
}
